import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kPluralCore } from "../../../Admin/kReusableCoreTS/kPluralCore";
import { AppTrAnswer, AppTrSettings, AppTranslate } from "./AppTranslate";
import { AppTranslateRules } from "./AppTranslateRules";
import { AppTranslator, IAppTrEarlier } from "./AppTranslator";
import { AppTranslateChip, IAppTrChipHost, IAppTrScreen } from "./AppTranslateChip";
import { AppTranslateFrame, IAppTrCopier, IAppTrFrameHost, IAppTrResent, ITrFrame } from "./AppTranslateFrame";
import { IAppLiveClock } from "./AppLiveFeed";

// AppTranslateLink - joins translation's pieces for ONE open contact view: the chip (`AppTranslateChip`), the
// frame's flag / Copy / failed-send buttons and the earlier link (`AppTranslateFrame`), and the visit's
// `/translate` state (`AppTranslator`). `AppMain` holds one and hands it a new view on every contact paint.
//
// ⛔ THE ONLY WAY OUT TO A SENDER IS `IAppTrPage.Resend`, reached only from a click on a failed bubble's Retry or
//    "Send in English" (`AppTranslateFrame.OnAct`). A save, a tab notice, an arriving batch never reaches it.
// ⭐ SWITCHED ON = the latest 200 are translated at once (`earlier` with `before` ""), then "Translate earlier
//    messages" for the next 200. While a batch runs the link ticks every second (the house rule).

/** What the page does for translation. ⛔ `Resend` is `AppComposer.SendText` - nothing else here sends. */
export interface IAppTrPage {
    Resend(Text: string, Translate: boolean, Then: IAppTrResent): string;
    Say(Text: string, Bad: boolean): void;
    /** Tell the other tabs this chat's settings changed. */
    Tell(Href: string): void;
}

export class AppTranslateLink implements IAppTrChipHost, IAppTrFrameHost, IAppTrEarlier {
    public Translator: AppTranslator;
    private Page: IAppTrPage;
    private Clock: IAppLiveClock;
    private Copier: IAppTrCopier;
    private Chip: AppTranslateChip | null = null;
    private Frame: AppTranslateFrame | null = null;
    private Href: string = "";
    private Asking: boolean = false;
    private AskedAt: number = 0;
    private Ticker: number = 0;
    private More: boolean = false;

    public constructor(Translator: AppTranslator, Page: IAppTrPage, Clock: IAppLiveClock, Copier: IAppTrCopier) {
        this.Translator = Translator;
        this.Page = Page;
        this.Clock = Clock;
        this.Copier = Copier;
    }

    /** A new contact view: the old chip and frame are stopped; the chip asks, the frame listens once it loads. */
    public Open = (Href: string, Title: string, Screen: IAppTrScreen, Frame: ITrFrame | null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Close();
            this.Href = nz(Href);
            if (this.Translator.CanAsk() === false) { return; }
            if (Frame !== null) {
                this.Frame = new AppTranslateFrame(this, this.Copier);
                (this.Frame as AppTranslateFrame).Attach(Frame as ITrFrame);
            }
            this.Chip = new AppTranslateChip(this.Translator, Screen, this, this.Href, Title);
            (this.Chip as AppTranslateChip).Start();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateLink.Open");
        }
    };

    /** The view was replaced (or left). */
    public Close = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Chip !== null) { (this.Chip as AppTranslateChip).Stop(); }
            if (this.Frame !== null) { (this.Frame as AppTranslateFrame).Stop(); }
            this.Chip = null;
            this.Frame = null;
            this.StopTicker();
            this.Asking = false;
            this.More = false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateLink.Close");
        }
    };

    /** The open chat's chip (the tab bus re-reads it). DOCUMENTED EXCEPTION: a field read. ERROR-SUPPRESSED-JUSTIFIED: pure. */
    public Current = (): AppTranslateChip | null => {
        return this.Chip;
    };

    /** A chip control was pressed (`data-wac-tract`). */
    public Act = (Action: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Chip !== null) { (this.Chip as AppTranslateChip).Act(Action); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateLink.Act");
        }
    };

    private Active = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return AppTranslate.Active(this.Translator.Book.Of(this.Href).Settings) && this.Translator.Book.Of(this.Href).Available === true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateLink.Active");
            return false;
        }
    };

    /** Offer "Translate earlier messages" when it has something to do (and nothing is running). */
    private Offer = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Frame === null) { return; }
            // ⭐ The page learns the owner's language with every state change, so "Send in <language>" is true.
            const Set: AppTrSettings | null = this.Translator.Book.Of(this.Href).Settings;
            if (Set !== null) { (this.Frame as AppTranslateFrame).NoteLang(nz((Set as AppTrSettings).MyLang)); }
            if (this.Asking === true) { return; }
            const Frame: AppTranslateFrame = this.Frame as AppTranslateFrame;
            Frame.OfferEarlier(AppTranslateRules.OfferEarlier(this.Active(), this.More, Frame.OlderUntranslated()));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateLink.Offer");
        }
    };

    /** `IAppTrFrameHost.Loaded`: the page is in - ask for the chip's state (shared) and then offer the link. */
    public Loaded = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Translator.Book.Of(this.Href).Known === true) { this.Offer(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateLink.Loaded");
        }
    };

    /** `IAppTrChipHost.Changed`: saved. The other tabs hear it; switched ON = the newest batch is translated now. */
    public Changed = (Href: string, TurnedOn: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Page.Tell(Href);
            if (Href !== this.Href) { return; }
            if (TurnedOn === true) { this.Ask(""); return; }
            this.Offer();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateLink.Changed");
        }
    };

    /** The chip heard its state - the earlier link follows it. */
    public Heard = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Offer();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateLink.Heard");
        }
    };

    /**
     * ⭐ THE "CHAT SETTINGS" DIALOG SAVED THIS CHAT'S TRANSLATION SETTINGS (its own controller on the same book): the
     * header's chip and line are drawn again from the book, then exactly what a save always did (`Changed`: the other
     * tabs are told; switched ON = the newest batch is translated). ⛔ Nothing is sent to anybody by it.
     */
    public SavedElsewhere = (Href: string, TurnedOn: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Href === this.Href && this.Chip !== null) { (this.Chip as AppTranslateChip).Paint(); }
            this.Changed(Href, TurnedOn);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateLink.SavedElsewhere");
        }
    };

    /** `IAppTrFrameHost.AskEarlier`: the owner pressed "Translate earlier messages". */
    public AskEarlier = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Frame === null || this.Asking === true) { return; }
            this.Ask(AppTranslateRules.BeforeOf((this.Frame as AppTranslateFrame).TranslatedMinutes()));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateLink.AskEarlier");
        }
    };

    /** One `earlier` batch, with the link ticking once a second until it answers. */
    private Ask = (Before: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Asking === true) { return; }
            this.Asking = true;
            this.AskedAt = Date.now();
            this.Tick();
            this.Translator.Earlier(this.Href, Before, this);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateLink.Ask");
        }
    };

    /** "Translating earlier messages… 7 s" - repainted every second while the batch runs. */
    public Tick = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Ticker = 0;
            if (this.Asking === false || this.Frame === null) { return; }
            const Seconds: number = Math.max(0, Math.round((Date.now() - this.AskedAt) / 1000));
            (this.Frame as AppTranslateFrame).PaintEarlier(true, "Translating earlier messages… " + String(Seconds) + " s");
            this.Ticker = this.Clock.Later(1000, this.Tick);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateLink.Tick");
        }
    };

    private StopTicker = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Ticker !== 0) { this.Clock.Cancel(this.Ticker); }
            this.Ticker = 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateLink.StopTicker");
        }
    };

    /** `IAppTrEarlier`: the batch answered - patched into the bubbles, the link offered again while there is more. */
    public Earlier = (Href: string, Answer: AppTrAnswer, Lost: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Href !== this.Href || this.Frame === null) { return; }
            this.Asking = false;
            this.StopTicker();
            if (Lost === true || Answer.Ok !== true) {
                let Why: string = Answer.Why;
                if (Why.length === 0) { Why = AppTranslateRules.EarlierLostWhy; }
                this.Page.Say(Why, Answer.Capped === false);
                this.Offer();
                return;
            }
            const Done: number = (this.Frame as AppTranslateFrame).Patch(Answer.Items);
            this.More = Answer.More;
            this.Offer();
            if (Answer.Capped === true) { this.Page.Say(AppTranslate.CappedWhy, false); return; }
            this.Page.Say("Translated " + String(Done) + " earlier message" + AppTranslateLink.Plural(Done) + ".", false);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateLink.Earlier");
        }
    };

    /** DOCUMENTED EXCEPTION: shared picker owns reporting; a latched gate keeps the former suffix. */
    private static Plural = (N: number): string => {
        const Picked: string = kPluralCore.Pick(N === 1 ? 1 : 2, { One: "message", Other: "messages" });
        if (Picked.length > 0) { return Picked.substring("message".length); }
        if (N === 1) { return ""; }
        return "s";
    };

    /** `IAppTrFrameHost.Resend` - ⛔ the owner's click on Retry / Send in English; the page's one sender. */
    public Resend = (Text: string, Translate: boolean, Then: IAppTrResent): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return this.Page.Resend(Text, Translate, Then);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateLink.Resend");
            return "";
        }
    };

    public Say = (Text: string, Bad: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Page.Say(Text, Bad);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateLink.Say");
        }
    };
}
